import { Routemize_Constants_SelectedSlotType } from '../../api/models/Routemize_Constants_SelectedSlotType';
import type { Routemize_DTOs_SlotSuggestionDTO } from '../../api/models/Routemize_DTOs_SlotSuggestionDTO';

/**
 * Which category the scheduling engine picked a suggestion for — the same
 * value the backend later persists on the booked appointment as `slotType`.
 */

/**
 * Before `SlotSuggestionDTO.slotType` existed the engine only tagged the
 * category as a `slot_type` match factor, and its label went through several
 * spellings ("CompanyRoutePreferenceBased", "CustomerPreferred",
 * "ProximityOptimal", "RoutePreference"). Endpoints that still answer with the
 * old shape are read through this.
 */
const fromMatchFactorLabel = (label: string): Routemize_Constants_SelectedSlotType => {
  if (label.includes('Company')) return Routemize_Constants_SelectedSlotType.COMPANY;
  if (label.includes('Customer')) return Routemize_Constants_SelectedSlotType.CUSTOMER;
  if (label.includes('Proximity') || label === 'RoutePreference') {
    return Routemize_Constants_SelectedSlotType.PROXIMITY;
  }
  return Routemize_Constants_SelectedSlotType.UNKNOWN;
};

export const resolveSlotType = (
  slot: Routemize_DTOs_SlotSuggestionDTO | undefined | null
): Routemize_Constants_SelectedSlotType => {
  if (!slot) return Routemize_Constants_SelectedSlotType.UNKNOWN;

  if (slot.slotType && slot.slotType !== Routemize_Constants_SelectedSlotType.UNKNOWN) {
    return slot.slotType;
  }

  const matchFactor = slot.matchFactors?.find(
    (factor) => (factor.key || '').toLowerCase() === 'slot_type'
  );
  return fromMatchFactorLabel(matchFactor?.label || '');
};

export const isCompanySlot = (slot: Routemize_DTOs_SlotSuggestionDTO | undefined | null): boolean =>
  resolveSlotType(slot) === Routemize_Constants_SelectedSlotType.COMPANY;

export const isCustomerPreferredSlot = (
  slot: Routemize_DTOs_SlotSuggestionDTO | undefined | null
): boolean => resolveSlotType(slot) === Routemize_Constants_SelectedSlotType.CUSTOMER;

export const isProximitySlot = (
  slot: Routemize_DTOs_SlotSuggestionDTO | undefined | null
): boolean => resolveSlotType(slot) === Routemize_Constants_SelectedSlotType.PROXIMITY;
